import cx from 'classnames'
import { getWrappedToken } from 'config/tokens'
import {
  OrderStatus,
  PendingOrderData,
  getPendingOrderKey,
  useSyntheticsEvents,
} from 'context/SyntheticsEvents'
import { MarketsInfoData } from 'domain/synthetics/markets'
import {
  isDecreaseOrderType,
  isIncreaseOrderType,
  isLimitOrderType,
  isLimitSwapOrderType,
  isMarketOrderType,
  isSwapOrderType,
} from 'domain/synthetics/orders'
import { TokensData } from 'domain/synthetics/tokens'
import { getSwapPathOutputAddresses } from 'domain/synthetics/trade'
import { useChainId } from 'rfx/lib/chains'
import { formatTokenAmount, formatUsd } from 'rfx/lib/numbers'
import { getByKey } from 'rfx/lib/objects'
import { useEffect, useMemo, useState } from 'react'
import { useToastAutoClose } from './useToastAutoClose'
import { getTriggerNameByOrderType } from 'domain/synthetics/positions'
import ExternalLink from 'components/ExternalLink/ExternalLink'
import { getExplorerUrl, getRpcUrl } from 'config/chains'
import {
  TransactionStatus,
  TransactionStatusType,
} from '@components/shared/TransactionStatus'
import { useTranslation } from 'react-i18next'
import { ExecutionTransactionStatus } from '@components/shared/ExecutionTransactionStatus'
import { ethers } from 'ethers'
import useWallet from 'rfx/lib/wallets/useWallet'
import { usePendingTxns } from 'rfx/lib/usePendingTxns'
import { helperToast } from 'rfx/lib/helperToast'
import { getErrorMessage, TxError } from 'rfx/lib/contracts/transactionErrors'

type Props = {
  toastTimestamp: number
  pendingOrderData: PendingOrderData
  marketsInfoData?: MarketsInfoData
  tokensData?: TokensData
  hideTxLink?: 'creation' | 'execution' | 'none'
}

async function speedUpTransaction(
  provider: ethers.providers.JsonRpcProvider,
  signer: ethers.providers.JsonRpcProvider,
  pendingTxHash: string,
  bumpPercent = 20,
) {
  const tx = await provider.getTransaction(pendingTxHash)

  if (!tx) {
    throw new Error('Transaction not found or already confirmed')
  }

  // Get current fee data
  const feeData = await provider.getFeeData()

  // Bump gas fees
  const newMaxFeePerGas = tx.maxFeePerGas
    ? tx.maxFeePerGas.mul(100 + bumpPercent).div(100)
    : feeData.maxFeePerGas
  const newMaxPriorityFeePerGas = tx.maxPriorityFeePerGas
    ? tx.maxPriorityFeePerGas.mul(100 + bumpPercent).div(100)
    : feeData.maxPriorityFeePerGas

  // Re-send the transaction with same nonce but higher gas
  const newTx = await signer.sendTransaction({
    // @ts-ignore
    to: tx.to,
    data: tx.data,
    value: tx.value,
    nonce: tx.nonce, // same nonce to replace
    gasLimit: tx.gasLimit,
    maxFeePerGas: newMaxFeePerGas,
    maxPriorityFeePerGas: newMaxPriorityFeePerGas,
  })

  return newTx.hash
}

export function OrderStatusNotification({
  pendingOrderData,
  marketsInfoData,
  tokensData,
  toastTimestamp,
  hideTxLink = 'none',
}: Props) {
  const { chainId } = useChainId()
  const wrappedNativeToken = getWrappedToken(chainId)
  const { orderStatuses, setOrderStatusViewed } = useSyntheticsEvents()

  const [orderStatusKey, setOrderStatusKey] = useState<string>()
  const contractOrderKey = pendingOrderData.orderKey
  const pendingOrderKey = useMemo(
    () => getPendingOrderKey(pendingOrderData),
    [pendingOrderData],
  )
  // @ts-ignore
  const { setPendingTxns } = usePendingTxns()

  const { signer } = useWallet()

  const { t } = useTranslation(['errors', 'trade'])
  const orderStatus = getByKey(orderStatuses, orderStatusKey)

  const hasError =
    Boolean(orderStatus?.cancelledTxnHash) &&
    pendingOrderData.txnType !== 'cancel'

  const orderData = useMemo(() => {
    if (
      !marketsInfoData ||
      !orderStatuses ||
      !tokensData ||
      !wrappedNativeToken
    ) {
      return undefined
    }

    const marketInfo = getByKey(marketsInfoData, pendingOrderData.marketAddress)
    const initialCollateralToken = getByKey(
      tokensData,
      pendingOrderData.initialCollateralTokenAddress,
    )
    const { outTokenAddress } = getSwapPathOutputAddresses({
      marketsInfoData,
      initialCollateralAddress: pendingOrderData.initialCollateralTokenAddress,
      swapPath: pendingOrderData.swapPath,
      wrappedNativeTokenAddress: wrappedNativeToken.address,
      shouldUnwrapNativeToken: pendingOrderData.shouldUnwrapNativeToken,
      isIncrease: isIncreaseOrderType(pendingOrderData.orderType),
    })
    const targetCollateralToken = getByKey(tokensData, outTokenAddress)

    return {
      ...pendingOrderData,
      marketInfo,
      initialCollateralToken,
      targetCollateralToken,
    }
  }, [
    marketsInfoData,
    orderStatuses,
    pendingOrderData,
    tokensData,
    wrappedNativeToken,
  ])

  const title = useMemo(() => {
    if (!orderData) {
      return `Unknown order`
    }

    if (isSwapOrderType(orderData.orderType)) {
      const {
        initialCollateralToken,
        targetCollateralToken,
        initialCollateralDeltaAmount,
        minOutputAmount,
      } = orderData

      const orderTypeText = isLimitSwapOrderType(orderData.orderType)
        ? `Limit Swap`
        : `Swap`

      return `${orderTypeText} ${formatTokenAmount(
        initialCollateralDeltaAmount,
        initialCollateralToken?.decimals,
        initialCollateralToken?.symbol,
      )} for ${formatTokenAmount(minOutputAmount, targetCollateralToken?.decimals, targetCollateralToken?.symbol)}`
    } else {
      const {
        txnType,
        marketInfo,
        sizeDeltaUsd,
        orderType,
        isLong,
        initialCollateralDeltaAmount,
        initialCollateralToken,
      } = orderData

      const longShortText = isLong ? `Long` : `Short`
      const positionText = `${marketInfo?.indexToken.symbol} ${longShortText}`

      if (sizeDeltaUsd.isZero()) {
        const symbol = orderData.shouldUnwrapNativeToken
          ? initialCollateralToken?.baseSymbol
          : initialCollateralToken?.symbol

        if (isIncreaseOrderType(orderType)) {
          return `Depositing ${formatTokenAmount(
            initialCollateralDeltaAmount,
            initialCollateralToken?.decimals,
            symbol,
          )} to ${positionText}`
        } else {
          return `Withdrawing ${formatTokenAmount(
            initialCollateralDeltaAmount,
            initialCollateralToken?.decimals,
            symbol,
          )} from ${positionText}`
        }
      } else {
        let orderTypeText = ''

        if (isMarketOrderType(orderType)) {
          orderTypeText = isIncreaseOrderType(orderType)
            ? `Increasing`
            : `Decreasing`
        } else {
          const txnTypeText = {
            create: `Create`,
            cancel: `Cancel`,
            update: `Update`,
          }[txnType]

          if (isLimitOrderType(orderType)) {
            orderTypeText = `${txnTypeText} limit order for`
          } else if (isDecreaseOrderType(orderType)) {
            orderTypeText = `${txnTypeText} ${getTriggerNameByOrderType(orderType, true)} order for`
          }
        }

        const sign = isIncreaseOrderType(orderType) ? '+' : '-'

        return `${orderTypeText} ${marketInfo?.indexToken?.symbol} ${longShortText}: ${sign}${formatUsd(
          sizeDeltaUsd,
        )}`
      }
    }
  }, [orderData])

  const creationStatus = useMemo(() => {
    let text = `Sending order request`
    let status: TransactionStatusType = 'loading'

    if (orderStatus?.createdTxnHash) {
      status = 'success'
      text = `Order request sent`
    }
    return (
      <>
        <TransactionStatus
          status={status}
          text={text}
          txnHash={
            hideTxLink !== 'creation' ? orderStatus?.createdTxnHash : undefined
          }
          onSpeedUp={async (hash) => {
            // eslint-disable-next-line no-console
            console.log({ hash }, 'speed up')
            try {
              const provider = new ethers.providers.JsonRpcProvider(
                getRpcUrl(chainId),
              )
              const newHash = await speedUpTransaction(
                provider,
                signer,
                hash || '',
                20,
              )
              // @ts-ignore
              // update your state with the new tx hash
              setPendingTxns((txns) =>
                // @ts-ignore
                txns.map((t) =>
                  t.createdTxnHash === hash
                    ? { ...t, createdTxnHash: newHash }
                    : t,
                ),
              )
            } catch (e: any) {
              // Check for “transaction not found” or “already confirmed”
              const errorMessage = e?.message || ''
              if (
                errorMessage.includes('Transaction not found') ||
                errorMessage.includes('already confirmed')
              ) {
                helperToast.info(
                  'Transaction is already confirmed or not found. Cannot speed up.',
                  { autoClose: 5000 },
                )
              } else {
                // fallback for other errors
                const { failMsg, autoCloseToast } = getErrorMessage(
                  chainId,
                  e as TxError,
                )
                helperToast.error(failMsg, { autoClose: autoCloseToast })
              }
            }
          }}
        />
      </>
    )
  }, [orderStatus?.createdTxnHash, hideTxLink])

  const executionStatus = useMemo(() => {
    if (!orderData || !isMarketOrderType(orderData?.orderType)) {
      return null
    }

    let text = `Fulfilling order request`
    let status: TransactionStatusType = 'muted'
    let txnHash: string | undefined

    if (orderStatus?.createdTxnHash) {
      status = 'loading'
    }

    if (orderStatus?.executedTxnHash) {
      text = `Order executed`
      status = 'success'
      txnHash = orderStatus?.executedTxnHash
    }

    if (orderStatus?.cancelledTxnHash) {
      text = `Order cancelled - ${t(`errors:${orderStatus?.reason}`) || orderStatus?.reason}`
      txnHash = orderStatus?.cancelledTxnHash

      if (orderData?.txnType !== 'cancel') {
        status = 'error'
      }
    }

    return (
      <ExecutionTransactionStatus
        status={status}
        txnHash={hideTxLink !== 'execution' ? txnHash : undefined}
        text={text}
        key={pendingOrderKey}
        orderStatus={orderStatus}
      />
    )
  }, [
    orderData,
    orderStatus?.cancelledTxnHash,
    orderStatus?.createdTxnHash,
    orderStatus?.executedTxnHash,
    orderStatus?.reason,
    t,
    orderStatus?.key,
    pendingOrderKey,
    hideTxLink,
  ])

  useEffect(
    function getOrderStatusKey() {
      if (orderStatusKey) {
        return
      }

      const matchedStatusKey = Object.values(orderStatuses).find(
        (orderStatus) => {
          if (orderStatus.isViewed) {
            return false
          }
          if (contractOrderKey && orderStatus.key === contractOrderKey) {
            return true
          }
          return (
            orderStatus.data &&
            getPendingOrderKey(orderStatus.data) === pendingOrderKey
          )
        },
      )?.key

      if (matchedStatusKey) {
        setOrderStatusKey(matchedStatusKey)
        setOrderStatusViewed(matchedStatusKey)
      }
    },
    [
      orderStatus,
      contractOrderKey,
      orderStatusKey,
      orderStatuses,
      pendingOrderKey,
      setOrderStatusViewed,
      toastTimestamp,
    ],
  )

  return (
    <div className={cx('StatusNotification', { error: hasError })}>
      <div className="StatusNotification-content">
        <div className="StatusNotification-title">{title}</div>

        <div className="StatusNotification-items flex flex-col gap-2 !text-xs !text-patek-neutral-celestial-grey-500">
          {creationStatus}
          {executionStatus}
        </div>
      </div>
    </div>
  )
}

export function OrdersStatusNotificiation({
  pendingOrderData,
  marketsInfoData,
  tokensData,
  toastTimestamp,
}: {
  pendingOrderData: PendingOrderData | PendingOrderData[]
  marketsInfoData?: MarketsInfoData
  tokensData?: TokensData
  toastTimestamp: number
  setPendingTxns: any
}) {
  const { chainId } = useChainId()
  const { orderStatuses: allOrderStatuses, setOrderStatusViewed } =
    useSyntheticsEvents()

  const pendingOrders = useMemo(
    () =>
      Array.isArray(pendingOrderData) ? pendingOrderData : [pendingOrderData],
    [pendingOrderData],
  )

  const [matchedOrderStatusKeys, setMatchedOrderStatusKeys] = useState<
    string[]
  >([])

  const matchedOrderStatuses = useMemo(
    () => matchedOrderStatusKeys.map((key) => allOrderStatuses[key]),
    [allOrderStatuses, matchedOrderStatusKeys],
  )

  const [ordersByPendingKey, ordersByContractKey] = useMemo(() => {
    const ordersByPendingKey = new Map<string, PendingOrderData>()
    const ordersByContractKey = new Map<string, PendingOrderData>()
    pendingOrders.forEach((order) => {
      if (order.orderKey) {
        ordersByContractKey.set(order.orderKey, order)
      }

      const key = getPendingOrderKey(order)
      ordersByPendingKey.set(key, order)
    })
    return [ordersByPendingKey, ordersByContractKey]
  }, [pendingOrders])

  useEffect(() => {
    Object.values(allOrderStatuses).forEach((orderStatus) => {
      const isPendingOrderMatch =
        orderStatus.data &&
        ordersByPendingKey.has(getPendingOrderKey(orderStatus.data))
      const isContractOrderMatch = ordersByContractKey.has(orderStatus.key)

      if (
        orderStatus.isViewed ||
        (!isPendingOrderMatch && !isContractOrderMatch)
      ) {
        return
      }

      setMatchedOrderStatusKeys((prev) => [...prev, orderStatus.key])
      setOrderStatusViewed(orderStatus.key)
    })
  }, [
    allOrderStatuses,
    ordersByPendingKey,
    ordersByContractKey,
    setOrderStatusViewed,
  ])

  const isCompleted = useMemo(() => {
    return pendingOrders.every((pendingOrder) => {
      const orderStatus = matchedOrderStatuses.find((status) => {
        const isPendingOrderMatch =
          status.data &&
          getPendingOrderKey(pendingOrder) === getPendingOrderKey(status.data)
        const isContractOrderMatch =
          pendingOrder.orderKey && pendingOrder.orderKey === status.key

        return isPendingOrderMatch || isContractOrderMatch
      })

      if (pendingOrder.txnType === 'create') {
        return isMarketOrderType(pendingOrder.orderType)
          ? Boolean(orderStatus?.executedTxnHash)
          : Boolean(orderStatus?.createdTxnHash)
      }
      if (pendingOrder.txnType === 'update') {
        return Boolean(orderStatus?.updatedTxnHash)
      }
      if (pendingOrder.txnType === 'cancel') {
        return Boolean(orderStatus?.cancelledTxnHash)
      }
    })
  }, [matchedOrderStatuses, pendingOrders])

  const createdTxnHashList = useMemo(() => {
    const uniqueHashSet = pendingOrders.reduce((acc, order) => {
      const orderStatus = findMatchedOrderStatus(matchedOrderStatuses, order)

      if (orderStatus?.createdTxnHash && order.txnType === 'create') {
        acc.add(orderStatus.createdTxnHash)
      }
      return acc
    }, new Set<string>())

    const uniqueHashList = Array.from(uniqueHashSet)

    if (uniqueHashList.length > 0) {
      return uniqueHashList
    }
  }, [matchedOrderStatuses, pendingOrders])

  useToastAutoClose(isCompleted, toastTimestamp)

  return (
    <div className="StatusNotification-wrapper ml-2">
      <div className="StatusNotification-list">
        {pendingOrders.map((order, index) => {
          return (
            <OrderStatusNotification
              key={index}
              pendingOrderData={order}
              marketsInfoData={marketsInfoData}
              tokensData={tokensData}
              toastTimestamp={toastTimestamp}
              hideTxLink={pendingOrders.length > 1 ? 'creation' : 'none'}
            />
          )
        })}
      </div>
      {pendingOrders.length > 1 && (
        <div className="StatusNotification-actions items-left flex flex-col justify-between">
          <div className="items-left inline-flex flex-col justify-start text-th-active">
            {createdTxnHashList?.map((txnHash) => (
              <ExternalLink
                key={txnHash}
                href={`${getExplorerUrl(chainId)}tx/${txnHash}`}
                className="text-th-active"
              >
                {`VIEW`}
              </ExternalLink>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function findMatchedOrderStatus(
  orderList: OrderStatus[],
  orderData: PendingOrderData,
) {
  const matchingOrderKey = getPendingOrderKey(orderData)

  return orderList.find((status) => {
    const isPendingOrderMatch =
      status.data && matchingOrderKey === getPendingOrderKey(status.data)
    const isContractOrderMatch =
      orderData.orderKey && orderData.orderKey === status.key

    return isPendingOrderMatch || isContractOrderMatch
  })
}
